import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { jwtDecode } from 'jwt-decode';
import ls from 'localstorage-slim';
import { Observable } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { ModalActionButton } from 'src/app/enums/modal-button-action.enum';
import { ModalContent } from 'src/app/models/modal-content.model';
import { ModalService } from 'src/app/services/modal/modal.service';
import { SpinnerService } from 'src/app/services/spinner/spinner.service';
import { sign } from 'src/app/shared/components/signature/signature';
import { generateTimestamp } from 'src/app/shared/utils/date.utils';
import {
  CLIENT_NAME,
  X_AUTH_NONCE,
  X_AUTH_SIGNATURE,
  X_CLIENT_ID,
  X_CLIENT_TYPE,
  X_PLATFORM_TYPE,
  acceptLanguage
} from '../../../app.constants';
import { AuthService } from '../services/auth.service';
import { SECRET_KEY } from './../../../app.constants';
import { PlatformType } from './../../../enums/platform-type.enum';
ls.config.encrypt = true;

@Injectable({
  providedIn: 'root'
})
export class RequestInterceptor implements HttpInterceptor {
  currentLang: string = '';
  publicKey: any;
  decoded: any;
  encryptCode: any;
  timeStamp!: string;
  totalRequests = 0;
  completedRequests = 0;
  options = {
    autoClose: true,
    keepAfterRouteChange: false
  };
  closeModal = new EventEmitter();

  constructor(
    private authService: AuthService,
    private modalService: ModalService,
    private spinner: SpinnerService,
    private router: Router,
    public translate: TranslateService
  ) {}

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    this.totalRequests++;
    if (
      !req.url.includes('/read') &&
      !req.url.includes('transactions/sign/') &&
      !req.url.includes('transactions/authorize-sign/') &&
      !req.url.includes('transactions/swift/sign/') &&
      !req.url.includes('transactions/swift/authorize-sign/') &&
      !req.url.includes('e-bills/authorize-invoice') &&
      !req.url.includes('e-bills/sign-invoice')
    ) {
      this.spinner.showSpinner();
    }
    if (
      this.authService.getToken() &&
      req.url.indexOf('/auth/otp-login') === -1
    ) {
      req = this.addHeadersToRequest(req, this.authService.getToken());
    }
    return next.handle(req).pipe(
      finalize(() => {
        this.completedRequests++;
        if (this.completedRequests === this.totalRequests) {
          this.spinner.hideSpinner();
          this.completedRequests = 0;
          this.totalRequests = 0;
        }
      }),
      catchError(err => {
        err.error
          ? this.authService.httpReqError.next(err.error.error_description)
          : '';
        this.spinner.hideSpinner();
        switch ((err as HttpErrorResponse).status) {
          case 400:
            return this.handleHttpError(err);
          case 401:
            return this.handle401Error(err);
          case 500:
            return this.handleHttpError(err);
          case 503:
            return this.handleHttpError(err);
          default:
            throw new Error(err);
        }
      })
    ) as any;
  }

  private addHeadersToRequest(request: HttpRequest<any>, token: string): any {
    this.currentLang = localStorage.getItem('language') || 'sl';
    this.decoded = jwtDecode(this.authService.getToken());
    this.timeStamp = generateTimestamp();
    const url = '/e-banking' + request.urlWithParams.split('/e-banking')[1];

    const signature = {
      jti: this.decoded.jti,
      clientId: ls.get('X-Client-Id'),
      clientTypeName: this.authService.getClientType(),
      userCode: this.decoded.userCode,
      method: request.method,
      url: url,
      body: request.body !== null ? JSON?.stringify(request.body) : '',
      nonce: this.timeStamp,
      secretKey: SECRET_KEY
    };

    this.encryptCode = Object.values(signature).join('|');

    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
        [X_AUTH_NONCE]: this.timeStamp,
        [X_AUTH_SIGNATURE]: sign(this.encryptCode),
        [X_CLIENT_ID]: this.authService.getClientId(),
        [X_CLIENT_TYPE]: this.authService.getClientType(),
        [X_PLATFORM_TYPE]: PlatformType.WEB,
        [acceptLanguage]: this.currentLang
      }
    });
  }

  handleHttpError(error: HttpErrorResponse): Observable<any> {
    if (error.url?.includes('dashboard')) {
      ls.remove(X_CLIENT_ID);
      ls.remove(CLIENT_NAME);
      ls.remove(X_CLIENT_TYPE);
      this.router.navigate(['login', 'select-profile']);
    }

    if (error.url?.includes('cards')) {
      this.router.navigateByUrl('/cards/(details:details)');
    }

    if (error.url?.includes('sign')) {
      this.router.navigateByUrl('/payments/(details:new-payment)');
    }

    let errModalContent: ModalContent = {
      title: 'common_alert_error_title',
      content: error.error?.error_description,
      buttons: [
        {
          title: 'popup_btn_ok',
          width: '200',
          action: ModalActionButton.Cancel
        }
      ]
    };
    this.modalService.getData(errModalContent);
    this.modalService.open();
    throw new Error(JSON.stringify(error));
  }

  handle401Error(error: HttpErrorResponse): Observable<any> {
    this.authService.logout();
    throw new Error(JSON.stringify(error));
  }
}
